import * as React from 'react';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Save, Link as LinkIcon, X, CheckCircle2, Send, FileText } from 'lucide-react';

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Field } from '@/components/ui/field';
import { Select } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from '@/components/ui/sonner';
import { env } from '@/lib/env';
import {
  useDeliverables,
  useUpdateDeliverable,
  type DeliverableRow,
  type DeliverableState,
  type ContentOrder,
  type OrderStage,
  type DeliveredUrl,
  type OrderState,
} from '@/lib/content';

interface OperatorOrder extends ContentOrder {
  tenant_slug: string | null;
  tenant_legal_name: string | null;
}

export const Route = createFileRoute('/_app/operator/content-orders')({
  staticData: { crumb: 'Zamówienia content' },
  beforeLoad: ({ context }) => {
    const ctx = context as { session: { role: string } };
    if (ctx.session.role !== 'operator') throw redirect({ to: '/' });
  },
  component: OperatorContentOrders,
});

const STAGE_OPTIONS: OrderStage[] = [
  'pending',
  'in_progress',
  'partial',
  'delivered',
  'indexed',
  'failed',
];

const STAGE_LABEL: Record<OrderStage, string> = {
  pending: 'Czeka',
  in_progress: 'W realizacji',
  partial: 'Częściowo dostarczone',
  delivered: 'Dostarczone',
  indexed: 'Zaindeksowane',
  failed: 'Niepowodzenie',
};

const STATE_LABEL: Record<OrderState, string> = {
  requested: 'requested',
  active: 'active',
  paused: 'paused',
  cancelled: 'cancelled',
  completed: 'completed',
};

function OperatorContentOrders() {
  return (
    <div className="space-y-6">
      <header>
        <h1 className="font-display text-2xl font-semibold tracking-tight text-text">
          Zamówienia content
        </h1>
        <p className="mt-1 text-sm text-text-muted">
          Zamówienia klientów + kolejka dostarczeń. Edycje w obu zakładkach
          są od razu widoczne w panelach klinik.
        </p>
      </header>

      <Tabs defaultValue="orders">
        <TabsList>
          <TabsTrigger value="orders">Zamówienia</TabsTrigger>
          <TabsTrigger value="pipeline">Pipeline (drafty + publikacja)</TabsTrigger>
        </TabsList>
        <TabsContent value="orders">
          <OrdersPanel />
        </TabsContent>
        <TabsContent value="pipeline">
          <PipelinePanel />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function OrdersPanel() {
  const [stageFilter, setStageFilter] = React.useState<OrderStage | ''>('');
  const orders = useQuery({
    queryKey: ['operator', 'content-orders', stageFilter],
    queryFn: async (): Promise<OperatorOrder[]> => {
      const url = new URL(`${env.apiBaseUrl}/api/operator/content/orders`, window.location.origin);
      if (stageFilter) url.searchParams.set('stage', stageFilter);
      const res = await fetch(url.toString(), { credentials: 'include' });
      if (!res.ok) throw new Error('failed_to_load');
      const j = (await res.json()) as { orders: OperatorOrder[] };
      return j.orders;
    },
    refetchOnWindowFocus: true,
  });

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Filtry</CardTitle>
          <CardDescription>Domyślnie pokazujemy wszystkie aktywne stadia.</CardDescription>
        </CardHeader>
        <CardContent>
          <Field label="Stage" htmlFor="stage-filter">
            <Select
              id="stage-filter"
              value={stageFilter}
              onChange={(e) => setStageFilter(e.target.value as OrderStage | '')}
            >
              <option value="">— wszystkie —</option>
              {STAGE_OPTIONS.map((s) => (
                <option key={s} value={s}>
                  {STAGE_LABEL[s]}
                </option>
              ))}
            </Select>
          </Field>
        </CardContent>
      </Card>

      {orders.isPending ? (
        <div className="space-y-3">
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-24 w-full" />
        </div>
      ) : (orders.data ?? []).length === 0 ? (
        <p className="rounded-md bg-surface p-4 text-sm text-text-muted ring-1 ring-black/5">
          Brak zamówień spełniających filtr.
        </p>
      ) : (
        <ul role="list" className="space-y-3">
          {(orders.data ?? []).map((o) => (
            <OperatorOrderRow key={o.id} order={o} />
          ))}
        </ul>
      )}
    </div>
  );
}

function OperatorOrderRow({ order }: { order: OperatorOrder }) {
  const qc = useQueryClient();
  const [stage, setStage] = React.useState<OrderStage>(order.stage);
  const [delivered, setDelivered] = React.useState<DeliveredUrl[]>(order.delivered_urls);
  const [newUrl, setNewUrl] = React.useState('');
  const [newDr, setNewDr] = React.useState('');
  const [newAnchor, setNewAnchor] = React.useState('');

  const save = useMutation({
    mutationFn: async () => {
      const res = await fetch(
        `${env.apiBaseUrl}/api/operator/content/orders/${order.id}/stage`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ stage, delivered_urls: delivered }),
        }
      );
      if (!res.ok) throw new Error('save_failed');
      return res.json();
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['operator', 'content-orders'] }),
  });

  const addDelivered = () => {
    if (!newUrl) return;
    setDelivered((d) => [
      ...d,
      {
        url: newUrl,
        dr: newDr ? Number(newDr) : undefined,
        anchor: newAnchor || undefined,
      },
    ]);
    setNewUrl('');
    setNewDr('');
    setNewAnchor('');
  };

  const removeDelivered = (idx: number) => {
    setDelivered((d) => d.filter((_, i) => i !== idx));
  };

  const markIndexed = (idx: number) => {
    setDelivered((d) =>
      d.map((row, i) =>
        i === idx ? { ...row, indexed_at: Math.floor(Date.now() / 1000) } : row
      )
    );
  };

  const isBacklink = order.package_kind === 'backlink_outreach';
  const landing = typeof order.metadata?.landing_page_url === 'string'
    ? order.metadata.landing_page_url
    : null;
  const anchorPrefs = Array.isArray(order.metadata?.anchor_texts)
    ? (order.metadata!.anchor_texts as string[])
    : [];

  return (
    <li className="rounded-md bg-surface p-4 ring-1 ring-black/5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p className="font-display text-base font-semibold text-text">
            {order.package_name}
          </p>
          <p className="mt-0.5 text-xs text-text-muted">
            {order.tenant_legal_name ?? order.tenant_slug ?? order.tenant_id} · Zlecone{' '}
            {new Date(order.requested_at * 1000).toLocaleString('pl-PL')} · State:{' '}
            <span className="font-mono">{STATE_LABEL[order.state]}</span>
          </p>
          {landing ? (
            <p className="mt-1 break-all text-xs text-text-muted">
              Landing: <span className="font-mono">{landing}</span>
            </p>
          ) : null}
          {anchorPrefs.length > 0 ? (
            <div className="mt-2 flex flex-wrap gap-1">
              {anchorPrefs.map((a) => (
                <span
                  key={a}
                  className="rounded-full bg-bg px-2 py-0.5 text-[10px] italic text-text-muted ring-1 ring-border"
                >
                  „{a}"
                </span>
              ))}
            </div>
          ) : null}
          {order.notes ? (
            <p className="mt-2 rounded-md bg-bg p-2 text-xs text-text ring-1 ring-border">
              <strong className="font-semibold">Uwagi klienta:</strong> {order.notes}
            </p>
          ) : null}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Select
            value={stage}
            onChange={(e) => setStage(e.target.value as OrderStage)}
            className="h-9"
          >
            {STAGE_OPTIONS.map((s) => (
              <option key={s} value={s}>
                {STAGE_LABEL[s]}
              </option>
            ))}
          </Select>
          <Button
            type="button"
            size="sm"
            onClick={() => save.mutate()}
            disabled={save.isPending}
          >
            <Save className="size-3.5" aria-hidden="true" /> Zapisz
          </Button>
        </div>
      </div>

      {isBacklink ? (
        <div className="mt-4 space-y-3 border-t border-border pt-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-text-subtle">
            Dostarczone linki ({delivered.length})
          </p>
          {delivered.length > 0 ? (
            <ul role="list" className="space-y-1.5">
              {delivered.map((d, i) => (
                <li
                  key={`${d.url}-${i}`}
                  className="flex flex-wrap items-center gap-2 rounded-md bg-bg p-2 text-xs ring-1 ring-border"
                >
                  <LinkIcon className="size-3 text-text-muted" aria-hidden="true" />
                  <a
                    href={d.url}
                    target="_blank"
                    rel="noreferrer"
                    className="flex-1 truncate font-mono text-accent hover:underline"
                  >
                    {d.url}
                  </a>
                  {d.dr != null ? (
                    <span className="font-mono text-text-subtle">DR {d.dr}</span>
                  ) : null}
                  {d.anchor ? (
                    <span className="italic text-text-subtle">„{d.anchor}"</span>
                  ) : null}
                  {d.indexed_at ? (
                    <span className="text-success">
                      ✓ {new Date(d.indexed_at * 1000).toLocaleDateString('pl-PL')}
                    </span>
                  ) : (
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={() => markIndexed(i)}
                    >
                      ✓ Zaindeksowany
                    </Button>
                  )}
                  <button
                    type="button"
                    aria-label="Usuń"
                    onClick={() => removeDelivered(i)}
                    className="text-text-subtle hover:text-error"
                  >
                    <X className="size-3" aria-hidden="true" />
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-xs text-text-muted">Brak dostarczonych linków.</p>
          )}

          <div className="grid gap-2 md:grid-cols-[2fr,80px,2fr,auto]">
            <Input
              type="url"
              placeholder="https://example.com/artykul"
              value={newUrl}
              onChange={(e) => setNewUrl(e.target.value)}
            />
            <Input
              type="number"
              placeholder="DR"
              value={newDr}
              onChange={(e) => setNewDr(e.target.value)}
            />
            <Input
              placeholder="anchor text"
              value={newAnchor}
              onChange={(e) => setNewAnchor(e.target.value)}
            />
            <Button type="button" variant="secondary" size="sm" onClick={addDelivered}>
              Dodaj link
            </Button>
          </div>
        </div>
      ) : null}
    </li>
  );
}

/* ─────────────── Pipeline panel — deliverable review queue ─────────────── */

const STATE_LABEL_DELIVERABLE: Record<DeliverableState, string> = {
  queued: 'W kolejce',
  in_progress: 'W realizacji',
  drafted: 'Draft do recenzji',
  approved: 'Zatwierdzone',
  published: 'Opublikowane',
  rejected: 'Odrzucone',
  failed: 'Nieudane',
};

const STATE_BADGE_VARIANT: Record<DeliverableState, 'soft' | 'outline' | 'success' | 'warning' | 'destructive' | 'default'> = {
  queued: 'outline',
  in_progress: 'soft',
  drafted: 'warning',
  approved: 'soft',
  published: 'success',
  rejected: 'destructive',
  failed: 'destructive',
};

const STATE_OPTIONS: Array<{ value: DeliverableState | ''; label: string; tally?: boolean }> = [
  { value: '', label: 'Wszystkie', tally: true },
  { value: 'queued', label: 'W kolejce', tally: true },
  { value: 'in_progress', label: 'W realizacji', tally: true },
  { value: 'drafted', label: 'Drafty do recenzji', tally: true },
  { value: 'approved', label: 'Zatwierdzone', tally: true },
  { value: 'published', label: 'Opublikowane' },
  { value: 'rejected', label: 'Odrzucone' },
  { value: 'failed', label: 'Nieudane' },
];

function PipelinePanel() {
  const [stateFilter, setStateFilter] = React.useState<DeliverableState | ''>('drafted');
  const allDeliverables = useDeliverables({});
  const filtered = useDeliverables({ state: stateFilter || undefined });

  // Tally by state from the full set so chip counters reflect the queue,
  // not the current filter.
  const tally = React.useMemo(() => {
    const counts: Partial<Record<DeliverableState | '_total', number>> = { _total: 0 };
    for (const d of allDeliverables.data ?? []) {
      counts[d.state] = (counts[d.state] ?? 0) + 1;
      counts._total = (counts._total ?? 0) + 1;
    }
    return counts;
  }, [allDeliverables.data]);

  return (
    <div className="space-y-6">
      <div className="-mx-1 flex flex-wrap items-center gap-2">
        {STATE_OPTIONS.map((opt) => {
          const count =
            opt.value === ''
              ? tally._total ?? 0
              : (tally[opt.value] ?? 0);
          const active = stateFilter === opt.value;
          return (
            <button
              key={opt.value || 'all'}
              type="button"
              onClick={() => setStateFilter(opt.value)}
              className={
                'inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium ring-1 transition ' +
                (active
                  ? 'bg-accent text-text-inverted ring-accent'
                  : 'bg-bg text-text-muted ring-border hover:ring-black/20')
              }
            >
              {opt.label}
              {opt.tally ? (
                <span className="tabular-nums opacity-70">{count}</span>
              ) : null}
            </button>
          );
        })}
      </div>

      {filtered.isPending ? (
        <div className="space-y-3">
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-32 w-full" />
        </div>
      ) : (filtered.data ?? []).length === 0 ? (
        <p className="rounded-md bg-surface p-4 text-sm text-text-muted ring-1 ring-black/5">
          {stateFilter
            ? `Brak deliverables w stanie „${STATE_LABEL_DELIVERABLE[stateFilter as DeliverableState]}".`
            : 'Brak deliverables w pipeline. Cron uruchamia się raz dziennie i tworzy nowe wpisy dla aktywnych zamówień.'}
        </p>
      ) : (
        <ul role="list" className="space-y-3">
          {(filtered.data ?? []).map((d) => (
            <DeliverableCard key={d.id} deliverable={d} />
          ))}
        </ul>
      )}
    </div>
  );
}

function DeliverableCard({ deliverable: d }: { deliverable: DeliverableRow }) {
  const update = useUpdateDeliverable();
  const [draftMd, setDraftMd] = React.useState(d.draft_md ?? '');
  const [draftUrl, setDraftUrl] = React.useState(d.draft_url ?? '');
  const [publishedUrl, setPublishedUrl] = React.useState(d.published_url ?? '');
  const [notes, setNotes] = React.useState(d.notes ?? '');
  const [kind, setKind] = React.useState(d.kind ?? '');

  // Re-sync local state when the row changes (e.g. after invalidation).
  React.useEffect(() => {
    setDraftMd(d.draft_md ?? '');
    setDraftUrl(d.draft_url ?? '');
    setPublishedUrl(d.published_url ?? '');
    setNotes(d.notes ?? '');
    setKind(d.kind ?? '');
  }, [d.id, d.updated_at]);

  const isPending = update.isPending;

  const saveDraft = async () => {
    try {
      await update.mutateAsync({
        id: d.id,
        patch: { draft_md: draftMd, draft_url: draftUrl || undefined, notes, kind },
      });
      toast.success('Draft zapisany');
    } catch (e) {
      toast.error((e as Error).message);
    }
  };

  const transition = async (next: DeliverableState, label: string) => {
    try {
      await update.mutateAsync({
        id: d.id,
        patch: {
          state: next,
          ...(draftMd !== (d.draft_md ?? '') ? { draft_md: draftMd } : {}),
          ...(notes !== (d.notes ?? '') ? { notes } : {}),
          ...(kind !== (d.kind ?? '') ? { kind } : {}),
          ...(next === 'published' && publishedUrl ? { published_url: publishedUrl } : {}),
        },
      });
      toast.success(label);
    } catch (e) {
      const msg = (e as Error).message;
      toast.error(msg === 'invalid_transition' ? 'Przejście niedozwolone z tego stanu.' : msg);
    }
  };

  const dueDate = new Date(d.due_at * 1000).toLocaleDateString('pl-PL');
  const overdue = d.due_at * 1000 < Date.now() && d.state !== 'published' && d.state !== 'failed' && d.state !== 'rejected';

  return (
    <li className="rounded-md bg-surface p-4 ring-1 ring-black/5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-baseline gap-2">
            <p className="font-display text-base font-semibold text-text">
              {d.order_package_name ?? d.order_package_id ?? 'Pakiet'}
              {d.kind ? <span className="ml-2 text-text-muted">— {d.kind}</span> : null}
            </p>
            <Badge variant={STATE_BADGE_VARIANT[d.state]}>{STATE_LABEL_DELIVERABLE[d.state]}</Badge>
            {overdue ? <Badge variant="destructive">Przeterminowane</Badge> : null}
          </div>
          <p className="mt-0.5 text-xs text-text-muted">
            {d.tenant_legal_name ?? d.tenant_slug ?? d.tenant_id} ·{' '}
            <span className="font-mono">{d.tenant_slug ?? ''}</span> · Termin {dueDate}
            {d.order_cadence_per_month
              ? ` · cadence ${d.order_cadence_per_month}/mies`
              : ''}
          </p>
        </div>
      </div>

      <div className="mt-3 grid gap-3 md:grid-cols-[1fr_280px]">
        <div className="space-y-3">
          <Field
            label="Kind (etykieta operatora)"
            htmlFor={`kind-${d.id}`}
            hint={'np. „blog: czyszczenie zębów u psa" lub „backlink: poradnik o profilaktyce"'}
          >
            <Input
              id={`kind-${d.id}`}
              value={kind}
              onChange={(e) => setKind(e.target.value)}
              placeholder="blog: temat artykułu"
            />
          </Field>
          <Field
            label="Draft (Markdown)"
            htmlFor={`draft-${d.id}`}
            hint={`${draftMd.length}/50000 znaków`}
          >
            <Textarea
              id={`draft-${d.id}`}
              value={draftMd}
              onChange={(e) => setDraftMd(e.target.value)}
              rows={Math.max(6, Math.min(14, draftMd.split('\n').length + 1))}
              placeholder="# Tytuł artykułu&#10;&#10;Wstęp..."
            />
          </Field>
          <Field label="Draft URL (Google Doc / Notion)" htmlFor={`draft-url-${d.id}`}>
            <Input
              id={`draft-url-${d.id}`}
              type="url"
              value={draftUrl}
              onChange={(e) => setDraftUrl(e.target.value)}
              placeholder="https://docs.google.com/document/d/…"
            />
          </Field>
          {d.state === 'approved' || d.state === 'published' ? (
            <Field label="Opublikowany URL" htmlFor={`pub-url-${d.id}`}>
              <Input
                id={`pub-url-${d.id}`}
                type="url"
                value={publishedUrl}
                onChange={(e) => setPublishedUrl(e.target.value)}
                placeholder="https://klinika.pl/blog/…"
              />
            </Field>
          ) : null}
          <Field label="Notatki" htmlFor={`notes-${d.id}`} hint="widoczne dla operatorów + audit">
            <Textarea
              id={`notes-${d.id}`}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              placeholder="Powód odrzucenia, uwagi po recenzji..."
            />
          </Field>
        </div>

        <aside className="space-y-2 rounded-md bg-bg p-3 ring-1 ring-border">
          <p className="text-xs font-semibold uppercase tracking-wide text-text-subtle">Akcje</p>
          <Button
            type="button"
            size="sm"
            variant="secondary"
            className="w-full"
            disabled={isPending}
            onClick={saveDraft}
          >
            <Save className="size-3.5" aria-hidden="true" /> Zapisz draft
          </Button>
          {d.state === 'queued' || d.state === 'rejected' ? (
            <Button
              type="button"
              size="sm"
              variant="secondary"
              className="w-full"
              disabled={isPending}
              onClick={() => transition('in_progress', 'Rozpoczęto')}
            >
              <FileText className="size-3.5" aria-hidden="true" /> Zaczynam pisać
            </Button>
          ) : null}
          {d.state === 'queued' || d.state === 'in_progress' || d.state === 'rejected' ? (
            <Button
              type="button"
              size="sm"
              className="w-full"
              disabled={isPending || !draftMd.trim()}
              onClick={() => transition('drafted', 'Draft gotowy do recenzji')}
            >
              <FileText className="size-3.5" aria-hidden="true" /> Oznacz jako draft
            </Button>
          ) : null}
          {d.state === 'drafted' ? (
            <>
              <Button
                type="button"
                size="sm"
                className="w-full"
                disabled={isPending}
                onClick={() => transition('approved', 'Zatwierdzono')}
              >
                <CheckCircle2 className="size-3.5" aria-hidden="true" /> Zatwierdź
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="w-full"
                disabled={isPending || !notes.trim()}
                onClick={() => transition('rejected', 'Odrzucono — dodaj notatkę')}
              >
                <X className="size-3.5" aria-hidden="true" /> Odrzuć
              </Button>
            </>
          ) : null}
          {d.state === 'approved' ? (
            <Button
              type="button"
              size="sm"
              className="w-full"
              disabled={isPending || !publishedUrl.trim()}
              onClick={() => transition('published', 'Opublikowano')}
            >
              <Send className="size-3.5" aria-hidden="true" /> Oznacz jako opublikowane
            </Button>
          ) : null}
          {d.state !== 'failed' && d.state !== 'published' ? (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className="w-full"
              disabled={isPending}
              onClick={() => transition('failed', 'Oznaczono jako nieudane')}
            >
              Oznacz jako nieudane
            </Button>
          ) : null}

          <div className="mt-2 space-y-1 border-t border-border pt-2 text-[11px] text-text-subtle">
            {d.drafted_at ? (
              <p>Draft: {new Date(d.drafted_at * 1000).toLocaleString('pl-PL')}</p>
            ) : null}
            {d.approved_at ? (
              <p>Zatw.: {new Date(d.approved_at * 1000).toLocaleString('pl-PL')}</p>
            ) : null}
            {d.published_at ? (
              <p>Publ.: {new Date(d.published_at * 1000).toLocaleString('pl-PL')}</p>
            ) : null}
            {d.published_url ? (
              <p>
                <a
                  href={d.published_url}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 text-accent hover:underline"
                >
                  <LinkIcon className="size-3" aria-hidden="true" /> Otwórz
                </a>
              </p>
            ) : null}
          </div>
        </aside>
      </div>
    </li>
  );
}
